/**
 * Exact decimal-string helpers for inventory quantities, unit costs and
 * values. Every quantity on the wire is NUMERIC(20,6) and every value is
 * NUMERIC(20,4), encoded as a JSON string with EXACTLY that many fractional
 * digits. Nothing in this module (or anything that calls it) converts a wire
 * decimal through `Number()` — canonicalization is string arithmetic only.
 */

const DECIMAL_RE = /^([+-]?)(\d+)(?:\.(\d*))?$/;

export interface DecimalParts {
  negative: boolean;
  integerDigits: string;
  fractionDigits: string;
}

/** Split a user-entered decimal into its exact parts, or null when malformed. */
export function parseDecimal(raw: string): DecimalParts | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const match = DECIMAL_RE.exec(trimmed);
  if (match === null) return null;
  return {
    negative: match[1] === "-",
    integerDigits: match[2] ?? "0",
    fractionDigits: match[3] ?? "",
  };
}

/**
 * Canonicalize to exactly `scale` fractional digits by string padding.
 * Returns null when the input is malformed or carries more precision than the
 * wire scale — extra digits are refused, never silently rounded away.
 */
export function toScaledDecimalString(raw: string, scale: number): string | null {
  const parts = parseDecimal(raw);
  if (parts === null) return null;
  if (parts.fractionDigits.length > scale) return null;
  const fraction = parts.fractionDigits.padEnd(scale, "0");
  const magnitude = scale === 0 ? parts.integerDigits : `${parts.integerDigits}.${fraction}`;
  if (isZeroDigits(parts.integerDigits, fraction)) return scale === 0 ? "0" : `0.${fraction}`;
  return parts.negative ? `-${magnitude}` : magnitude;
}

function isZeroDigits(integerDigits: string, fractionDigits: string): boolean {
  return !/[1-9]/.test(integerDigits + fractionDigits);
}

/** True when the canonical decimal string is exactly zero. */
export function isZeroDecimal(value: string): boolean {
  const parts = parseDecimal(value);
  if (parts === null) return false;
  return isZeroDigits(parts.integerDigits, parts.fractionDigits);
}

/** True when the canonical decimal string is strictly greater than zero. */
export function isPositiveDecimal(value: string): boolean {
  const parts = parseDecimal(value);
  if (parts === null) return false;
  return !parts.negative && !isZeroDigits(parts.integerDigits, parts.fractionDigits);
}

/** True when the canonical decimal string is strictly less than zero. */
export function isNegativeDecimal(value: string): boolean {
  const parts = parseDecimal(value);
  if (parts === null) return false;
  return parts.negative && !isZeroDigits(parts.integerDigits, parts.fractionDigits);
}

/** Flip the sign of a canonical decimal string without touching its digits. */
export function negateDecimalString(value: string): string {
  if (isZeroDecimal(value)) return value;
  return value.startsWith("-") ? value.slice(1) : `-${value}`;
}

/** Drop a leading sign, keeping the exact digits. */
export function absDecimalString(value: string): string {
  return value.startsWith("-") ? value.slice(1) : value;
}
